import {
  electoralListChooserCandidateSuffix,
  electoralListChooserColumnsSuffix,
  electoralListChooserListNameSuffix,
  ElectoralListIdMetaKey,
  VoteType,
  VoteTypeMetaKey,
  WidgetType,
  WidgetTypeInternalMetaKey,
} from '@framework/engine';
import {
  forEachSchemaNode,
  IForEachSchemaOptions,
  Schema,
  yup,
} from '@framework/validation';
import { isEmpty } from 'lodash';

export interface IVoteExtracted {
  idLista: string;
  idMembro: string;
}

/**
 * Tipo di votazione del form, dichiarato dal pulsante di voto sul proprio schema.
 * Null se il form non contiene un pulsante di voto (nessun voto da estrarre).
 */
function getVoteType(
  schema: yup.ISchema<any>,
  options: IForEachSchemaOptions,
): VoteType | null {
  let voteType: VoteType | null = null;

  forEachSchemaNode(schema, options, node => {
    if (!(node.schema instanceof Schema) || node.schema.spec.strip) {
      return;
    }
    const meta = node.schema.meta?.();
    if (meta?.[WidgetTypeInternalMetaKey] === WidgetType.Vote) {
      voteType = meta[VoteTypeMetaKey] ?? VoteType.CTS;

      return false;
    }
  });

  return voteType;
}

/**
 * Estrae i voti da tutti i widget candidato con valori selezionati
 */
function extractCandidateVotes(
  schema: yup.ISchema<any>,
  options: IForEachSchemaOptions,
  votes: Array<IVoteExtracted>,
): void {
  forEachSchemaNode(schema, options, node => {
    if (isCandidateWidget(node)) {
      const vote = extractVoteFromCandidateNode(node);
      if (vote) {
        votes.push(vote);
      }
    }
  });
}

/**
 * Estrae i voti dai widget di scelta lista elettorale: ogni lista selezionata
 * produce un voto con il candidato scelto (sotto-campo `@candidateId`). Il primo
 * candidato è impostato di default in UI e le liste senza candidati non sono
 * selezionabili, quindi una lista selezionata ha sempre un candidato; in assenza
 * di candidato il voto non viene emesso.
 */
function extractElectoralListChooserVotes(
  schema: yup.ISchema<any>,
  options: IForEachSchemaOptions,
  votes: Array<IVoteExtracted>,
): void {
  // Suffisso dei sotto-campi candidato del widget di scelta lista: contratto
  // condiviso con theme-mic (electoralListChooserCandidateFieldName lo applica
  // costruendo il nome completo del campo).
  const candidateSuffix = electoralListChooserCandidateSuffix;
  // Indicizzati per path del campo lista: il sotto-campo candidato condivide lo
  // stesso path una volta rimosso il suffisso, così lista e candidato si
  // ricongiungono.
  const selectedListByPath: Record<string, string> = {};
  const candidateByPath: Record<string, string> = {};

  forEachSchemaNode(schema, options, node => {
    if (
      !(node.schema instanceof Schema) ||
      !node.path ||
      node.schema.spec.strip
    ) {
      return;
    }

    // Sotto-campo candidato: il valore è il membro scelto. Va intercettato prima
    // del campo principale perché il FormBuilder marca anche i sotto-campi con il
    // tipo widget del padre.
    if (node.path.endsWith(candidateSuffix)) {
      if (!isEmpty(node.value)) {
        const listPath = node.path.slice(0, -candidateSuffix.length);
        candidateByPath[listPath] = node.value;
      }
      return;
    }

    // I sotto-campi colonna del candidato (`@columns` e i suoi figli) e il nome
    // della lista selezionata (`@listName`) sono marcati col meta del widget dal
    // FormBuilder: vanno esclusi, altrimenti verrebbero scambiati per liste
    // selezionate (voti spuri), come fa il ramo CTS escludendo `@columns`.
    if (
      node.path.includes(electoralListChooserColumnsSuffix) ||
      node.path.endsWith(electoralListChooserListNameSuffix)
    ) {
      return;
    }

    // Campo principale: il valore è la lista scelta dall'utente.
    const meta = node.schema.meta?.();
    if (
      meta?.[WidgetTypeInternalMetaKey] === WidgetType.ElectoralListChooser &&
      !isEmpty(node.value)
    ) {
      selectedListByPath[node.path] = node.value;
    }
  });

  for (const [listPath, listId] of Object.entries(selectedListByPath)) {
    const memberId = candidateByPath[listPath];
    if (memberId) {
      votes.push({ idLista: listId, idMembro: memberId });
    }
  }
}

/**
 * Verifica se un nodo rappresenta un widget candidato valido
 */
function isCandidateWidget(node: any): boolean {
  return (
    node.schema instanceof Schema &&
    node.schema.meta?.()?.[WidgetTypeInternalMetaKey] ===
      WidgetType.Candidate &&
    !node.schema.spec.strip &&
    node.path &&
    !node.path.includes('@columns') // Esclude i metadati delle colonne
  );
}

/**
 * Estrae un voto da un nodo widget candidato se valido
 */
function extractVoteFromCandidateNode(node: any): IVoteExtracted | null {
  const widgetMeta = node.schema.meta?.();
  const electoralListId = widgetMeta?.[ElectoralListIdMetaKey];
  const candidateValue = node.value;

  // Verifica che entrambi i valori siano stringhe valide e non vuote
  if (!isEmpty(electoralListId) && !isEmpty(candidateValue)) {
    return {
      idLista: electoralListId,
      idMembro: candidateValue,
    };
  }

  return null;
}

/**
 * Estrae i voti dal form in base al tipo di votazione dichiarato dal pulsante di
 * voto: dai widget candidato (CTS) oppure dai widget di scelta lista (CS).
 */
export function extractVotes(
  schema: yup.ISchema<any>,
  options: IForEachSchemaOptions,
): Array<IVoteExtracted> {
  const votes: Array<IVoteExtracted> = [];

  const voteType = getVoteType(schema, options);
  if (voteType === null) {
    return votes;
  }

  if (voteType === VoteType.CS) {
    extractElectoralListChooserVotes(schema, options, votes);
  } else {
    extractCandidateVotes(schema, options, votes);
  }

  return votes;
}
